<template>
  <div class="period_picker" @click.stop @mousedown.stop>
    <div class="year_row" @click.stop="yearPopupOpen = !yearPopupOpen">
      <span class="text-sm font-semibold text_color">{{ currentYear }}</span>
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" :class="['size-4 year_arrow', yearPopupOpen && 'year_arrow_open']">
        <polyline points="6 9 12 15 18 9" />
      </svg>

      <div v-if="yearPopupOpen" class="year_popup" @click.stop>
        <div v-for="y in yearList" :key="y" :class="['year_popup_row', y === currentYear && 'year_popup_row_active']" @click="selectYear(y)">
          {{ y }}
        </div>
      </div>
    </div>

    <div class="month_list">
      <div v-for="(m, idx) in MONTH_NAMES" :key="m" :class="['month_row', idx === currentMonth && 'month_row_active']" @click="selectMonth(idx)">
        {{ m }}
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

const props = defineProps({
  year: { type: Number, required: true },
  month: { type: Number, required: true },
});

const emit = defineEmits(['update:year', 'update:month', 'select']);

const yearPopupOpen = ref(false);

const currentYear = computed(() => props.year);
const currentMonth = computed(() => props.month);

const yearList = computed(() => {
  const y = props.year;
  const out = [];
  for (let i = y - 3; i <= y + 3; i++) out.push(i);
  return out;
});

const selectYear = (y) => {
  emit('update:year', y);
  yearPopupOpen.value = false;
};

const selectMonth = (idx) => {
  emit('update:month', idx);
  emit('select', { year: props.year, month: idx });
};
</script>

<style scoped>
.period_picker {
  position: relative;
  background-color: var(--dropdown-bg, #fff);
  border: 1px solid var(--white-opacity-8, rgba(0, 0, 0, 0.1));
  border-radius: 10px;
  padding: 8px;
  min-width: 180px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
  font-family: 'Proxima Nova', sans-serif;
}

.year_row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  position: relative;
  margin-bottom: 4px;
  border-bottom: 1px solid var(--white-opacity-8, rgba(0, 0, 0, 0.06));
}

.year_row:hover {
  background-color: var(--white-opacity-6, rgba(0, 0, 0, 0.04));
}

.year_arrow {
  color: var(--secondary-color);
  transition: transform 0.15s ease;
}

.year_arrow_open {
  transform: rotate(180deg);
}

.year_popup {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  background-color: var(--dropdown-bg, #fff);
  border: 1px solid var(--white-opacity-8, rgba(0, 0, 0, 0.1));
  border-radius: 8px;
  padding: 4px;
  max-height: 200px;
  overflow-y: auto;
  z-index: 10;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
}

.year_popup_row {
  padding: 6px 10px;
  border-radius: 4px;
  cursor: pointer;
  user-select: none;
  font-size: 13px;
  color: var(--text-color);
}

.year_popup_row:hover,
.year_popup_row_active {
  background-color: var(--white-opacity-6, rgba(0, 0, 0, 0.05));
}

.month_list {
  max-height: 240px;
  overflow-y: auto;
}

.month_row {
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  font-size: 14px;
  color: var(--text-color);
}

.month_row:hover {
  background-color: var(--white-opacity-6, rgba(0, 0, 0, 0.05));
}

.month_row_active {
  background-color: var(--white-opacity-8, rgba(0, 0, 0, 0.08));
  font-weight: 600;
}
</style>
